import React, { useState, useCallback } from "react";
import cn from "classnames";

import { useSelector } from "react-redux";
import Field from "containers/Field";
import { selectFieldValue, selectEntityData } from "state/selectors/data";
import { NS_SCHEMA, NS_SCHEMA_FIELDS } from "state/selectors/schema";
import { useField } from "state/hooks/useField";
import Popover from "components/Popover";
import IconButton from "components/IconButton";
import SelectList, { SelectListItem } from "playground/ui/SelectList";
import Button from "components/Button";
import "./style.css";

const selectFieldIndex = (store, parentEntityType, parentEntityId) => {
    const resourceType = selectFieldValue(
        store,
        parentEntityType,
        parentEntityId,
        "resource_type"
    );
    if (!resourceType) return null;
    const allSchemas = Object.values(store.data[NS_SCHEMA] || {});
    const entriesSchema = allSchemas.find(
        (s) => s.slug === "entries" && !s.subtype_of
    );
    const typeSchema = entriesSchema
        ? allSchemas.find(
              (s) =>
                  s.subtype_of === entriesSchema.id && s.slug === resourceType
          )
        : null;
    const schemaId = typeSchema?.id || entriesSchema?.id;
    return selectFieldValue(store, NS_SCHEMA, schemaId, "field_index") || null;
};

const selectSchemaFieldSlugs = (store, parentEntityType, parentEntityId) => {
    const fieldIndex = selectFieldIndex(
        store,
        parentEntityType,
        parentEntityId
    );
    return fieldIndex ? Object.keys(fieldIndex) : null;
};

const selectSchemaFieldDef = (
    store,
    parentEntityType,
    parentEntityId,
    fieldSlug
) => {
    if (!fieldSlug) return null;
    const fieldIndex = selectFieldIndex(
        store,
        parentEntityType,
        parentEntityId
    );
    if (!fieldIndex?.[fieldSlug]) return null;
    return selectEntityData(store, NS_SCHEMA_FIELDS, fieldIndex[fieldSlug]);
};

function SlugValueRenderer({ id }) {
    return <div>{id}</div>;
}

function FieldMappingItem({
    id,
    type,
    onDelete,
    parentEntityType,
    parentEntityId,
}) {
    const [showDefaultValue, setShowDefaultValue] = useState(false);

    const schemaFieldIds = useSelector((store) =>
        selectSchemaFieldSlugs(store, parentEntityType, parentEntityId)
    );
    const { value: active, onChange: setActive } = useField(type, id, "active");
    const { value: fieldId } = useField(type, id, "field_id");
    const { value: defaultValue, onChange: setDefaultValue } = useField(
        type,
        id,
        "default_value"
    );

    const fieldDef = useSelector((store) =>
        selectSchemaFieldDef(store, parentEntityType, parentEntityId, fieldId)
    );

    const toggleActive = useCallback(
        () => setActive(!active),
        [setActive, active]
    );

    return (
        <div
            className={cn("field-mapping-item rows gap-xs", {
                "is-disabled": !active,
            })}
        >
            <div className={"cols cols-middle gap-sm"}>
                <div className={"cols grow"}>
                    <Field
                        entityType={type}
                        entityId={id}
                        id={"field_id"}
                        fieldType={"select"}
                        label={"Field"}
                        ValueRenderer={SlugValueRenderer}
                        fetchProps={
                            schemaFieldIds
                                ? {
                                      type: NS_SCHEMA_FIELDS,
                                      items: schemaFieldIds,
                                  }
                                : undefined
                        }
                    />
                </div>
                {showDefaultValue ? (
                    <Field
                        entityType={type}
                        entityId={id}
                        id={"default_value"}
                        fieldType={fieldDef?.type || "text"}
                        label={"Default value"}
                        {...(fieldDef?.properties || {})}
                    />
                ) : defaultValue ? (
                    <div className={"field-mapping-default-value"}>
                        {defaultValue}
                    </div>
                ) : null}
                <Popover
                    trigger={({ onClick }) => (
                        <IconButton icon={"mui-more_vert"} onClick={onClick} />
                    )}
                    content={({ onClose }) => (
                        <SelectList>
                            <SelectListItem
                                label={active ? "Disable" : "Enable"}
                                onClick={() => {
                                    toggleActive();
                                    onClose();
                                }}
                            />
                            <SelectListItem
                                label={"Edit default value"}
                                onClick={() => {
                                    setShowDefaultValue((v) => !v);
                                    onClose();
                                }}
                            />
                            <SelectListItem
                                label={"Remove default value"}
                                onClick={() => {
                                    setDefaultValue(null);
                                    setShowDefaultValue(false);
                                    onClose();
                                }}
                            />
                            <SelectListItem
                                label={"Remove"}
                                onClick={() => {
                                    onDelete(type, id);
                                    onClose();
                                }}
                            />
                        </SelectList>
                    )}
                />
            </div>
        </div>
    );
}

export function FieldMappingRenderer(props) {
    const { items, type, onCreate, onDelete, entityType, entityId } = props;
    return (
        <div className={"rows gap-sm"}>
            {items &&
                items.map((id) => (
                    <FieldMappingItem
                        key={id}
                        id={id}
                        type={type}
                        onDelete={onDelete}
                        parentEntityType={entityType}
                        parentEntityId={entityId}
                    />
                ))}
            <div>
                <Button onClick={onCreate} size={"small"}>
                    + Add mapping
                </Button>
            </div>
        </div>
    );
}

export default FieldMappingRenderer;
